import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSnsAccountsOptions,
  getSnsAccountsQueryKey,
  searchSnsAccountsOptions,
  searchSnsAccountsQueryKey,
  createSnsAccountMutation,
  updateSnsAccountMutation,
  deleteSnsAccountMutation,
  importFromCsvMutation,
  exportToCsvOptions,
  exportToCsvQueryKey,
} from '@/shared/api';
import { logger } from '@/shared/lib';

export interface AccountQueryParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: string;
}

export interface SearchAccountParams extends AccountQueryParams {
  accountId?: string;
  platform?: 'TIKTOK' | 'INSTAGRAM' | 'YOUTUBE';
}

/**
 * Query factory for accounts
 */
export const accountQueries = {
  all: (params?: AccountQueryParams) => getSnsAccountsOptions({ query: params }),
  allKey: (params?: AccountQueryParams) => getSnsAccountsQueryKey({ query: params }),
  search: (params?: SearchAccountParams) => searchSnsAccountsOptions({ query: params }),
  searchKey: (params?: SearchAccountParams) => searchSnsAccountsQueryKey({ query: params }),
};

/**
 * Hook to fetch all SNS accounts with pagination
 */
export const useAccounts = (params?: AccountQueryParams, enabled: boolean = true) => {
  return useQuery({
    ...accountQueries.all(params),
    enabled: enabled && !!params,
  });
};

/**
 * Hook to search SNS accounts with pagination
 */
export const useSearchAccounts = (params?: SearchAccountParams, enabled: boolean = true) => {
  return useQuery({
    ...accountQueries.search(params),
    enabled: enabled && !!params,
  });
};

/**
 * Hook to create a new SNS account
 */
export const useCreateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    ...createSnsAccountMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueries.allKey() });
      queryClient.invalidateQueries({ queryKey: accountQueries.searchKey() });
    },
  });
};

/**
 * Hook to update an existing SNS account
 */
export const useUpdateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    ...updateSnsAccountMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueries.allKey() });
      queryClient.invalidateQueries({ queryKey: accountQueries.searchKey() });
    },
  });
};

/**
 * Hook to delete an SNS account
 */
export const useDeleteAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    ...deleteSnsAccountMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueries.allKey() });
      queryClient.invalidateQueries({ queryKey: accountQueries.searchKey() });
    },
  });
};

/**
 * Hook to import accounts from CSV file
 */
export const useImportCsv = () => {
  const queryClient = useQueryClient();

  return useMutation({
    ...importFromCsvMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueries.allKey() });
      queryClient.invalidateQueries({ queryKey: accountQueries.searchKey() });
      logger.success('CSV 파일을 성공적으로 가져왔습니다');
    },
    onError: (error) => {
      logger.error('CSV 가져오기 실패:', error);
    },
  });
};

/**
 * Hook to export accounts to CSV file
 */
export const useExportCsv = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params?: any) => {
      const response = await queryClient.fetchQuery(exportToCsvOptions({ query: params }));
      const csvData = response as string;

      // Create download link
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sns-accounts-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return csvData;
    },
    onSuccess: () => {
      logger.success('CSV 내보내기가 완료되었습니다');
    },
    onError: (error) => {
      logger.error('CSV 내보내기 실패:', error);
    },
  });
};
